import { defineStore } from 'pinia'
import {
  entityDialogDefaults,
  deriveEntityDialogEntityId,
} from '~/cynqueModules/store/entityDialogState'
import type { TEntityId } from '~/types/models'

/**
 * The dialog behind every create / edit / delete flow in the app. Converted from
 * store/entityDialog.js, the last Vuex module.
 *
 * TWENTY-NINE OF THE THIRTY GETTERS ARE GONE, deliberately. Every one was a pure
 * pass-through of a same-named state field — the shape that breaks silently
 * under Pinia, where a getter shadows the field, makes it readonly, and swallows
 * every write an action makes to it. The fields answer every caller unchanged:
 * the facade resolves `entityDialog/entityName` against the whole store, and a
 * state field is on the store.
 *
 * The one getter kept is `entityId`, the only one with real derivation — an
 * explicit override wins, else the loaded entity's id, else null. It was
 * extracted to entityDialogState.ts before the migration precisely so it would
 * carry over untouched, and it does.
 *
 * `showEntityDialog` and `showSubentityDialog` lose a `=== true` coercion along
 * with their getters. A no-op: both default to false and their only writers are
 * the setters below, which callers pass booleans.
 */
interface IEntityDialogState {
  showEntityDialog: boolean
  entityNameTitlePrefixOverride: string | null
  entityNameTitleOverride: string | null
  subtitle: string | null
  message: string | null
  saveButtonCaptionVerbOverride: string | null
  deleteButtonCaptionsVerbOverride: string | null
  desiredAction: string | null
  preRequestProcessor: any
  afterRequestCallback: any
  storeEditedInputDataAsJsonInProperty: string | null
  entityName: string | null
  inputFieldsEntityNameOverride: string | null | undefined
  inputFieldsOverride: any
  entityIdOverride: TEntityId | null | undefined
  entityData: any
  entityParentObjectForCreate: any
  allowAddAnother: boolean
  startInViewMode: boolean
  disableEditButton: boolean
  suppressFocusOnFirstField: boolean
  doSubdueMessage: boolean
  doAlignMessageWithTitle: boolean
  showSubentityDialog: boolean
  subentityName: string | null
  subentityNameTitleOverride: string | null
  subentityInputFieldsEntityNameOverride: string | null | undefined
  subentityReturnValue: any
  subentityAfterRequestProcessor: any
  subentityAfterRequestCallbacks: any
  subentityParentObjectForCreate: any
  valuesForSubentityFieldsWhoseValuesToSetFromEntityFields: any
}

export const useEntityDialogStore = defineStore('entityDialog', {
  // entityDialogDefaults stays the single source of truth for the initial state,
  // as it has been since it was extracted. The cast widens the literal types the
  // defaults object infers (`null`, `false`) to what the fields actually hold.
  state: (): IEntityDialogState =>
    ({ ...entityDialogDefaults } as IEntityDialogState),

  getters: {
    entityId: (state): TEntityId | null => deriveEntityDialogEntityId(state),
  },

  actions: {
    setShowEntityDialog(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating showEntityDialog from ${this.showEntityDialog} to ${stateToSet}`
      )
      this.showEntityDialog = stateToSet
    },

    setEntityNameTitlePrefixOverride(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating entityDialogEntityNameTitlePrefixOverride from '${this.entityNameTitlePrefixOverride}' to ${override}`
      )
      this.entityNameTitlePrefixOverride = override
    },
    clearEntityNameTitlePrefixOverride(): void {
      this.setEntityNameTitlePrefixOverride(null)
    },

    setEntityNameTitleOverride(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating entityDialogEntityNameTitleOverride from '${this.entityNameTitleOverride}' to ${override}`
      )
      this.entityNameTitleOverride = override
    },
    clearEntityNameTitleOverride(): void {
      this.setEntityNameTitleOverride(null)
    },

    setSaveButtonCaptionVerbOverride(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating saveButtonCaptionVerbOverride from '${this.saveButtonCaptionVerbOverride}' to '${override}'`
      )
      this.saveButtonCaptionVerbOverride = override
    },
    clearSaveButtonCaptionVerbOverride(): void {
      this.setSaveButtonCaptionVerbOverride(null)
    },

    setDeleteButtonCaptionsVerbOverride(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating deleteButtonCaptionsVerbOverride from '${this.deleteButtonCaptionsVerbOverride}' to ${override}`
      )
      this.deleteButtonCaptionsVerbOverride = override
    },
    clearDeleteButtonCaptionsVerbOverride(): void {
      this.setDeleteButtonCaptionsVerbOverride(null)
    },

    setSubtitle(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating subtitle from '${this.subtitle}' to ${override}`
      )
      this.subtitle = override
    },
    clearSubtitle(): void {
      this.setSubtitle(null)
    },

    setMessage(message: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating message from '${this.message}' to ${message}`
      )
      this.message = message
    },
    clearMessage(): void {
      this.setMessage(null)
    },

    // Writes `desiredAction`; the Vuex mutation behind it was named
    // SET_REQUEST_VERB. Action and field names preserved as they were.
    setDesiredAction(verb: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating desiredAction from '${this.desiredAction}' to ${verb}`
      )
      this.desiredAction = verb
    },
    clearDesiredAction(): void {
      this.setDesiredAction(null)
    },

    setPreRequestProcessor(fn: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating preRequestProcessor from '${this.preRequestProcessor}' to`,
        fn
      )
      this.preRequestProcessor = fn
    },
    clearPreRequestProcessor(): void {
      this.setPreRequestProcessor(undefined)
    },

    setAfterRequestCallback(callback: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating afterRequestCallback from '${this.afterRequestCallback}' to`,
        callback
      )
      this.afterRequestCallback = callback
    },
    clearAfterRequestCallback(): void {
      this.setAfterRequestCallback(undefined)
    },

    setStoreEditedSpecsDataAsJsonInProperty(propertyName: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating storeEditedInputDataAsJsonInProperty from '${this.storeEditedInputDataAsJsonInProperty}' to`,
        propertyName
      )
      this.storeEditedInputDataAsJsonInProperty = propertyName
    },
    clearStoreEditedSpecsDataAsJsonInProperty(): void {
      this.setStoreEditedSpecsDataAsJsonInProperty(null)
    },

    setEntityName(entityName: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating entityName from ${this.entityName} to ${entityName}`
      )
      this.entityName = entityName
    },
    clearEntityName(): void {
      this.setEntityName(null)
    },

    setOverrideEntityNameForInputFields(entityName: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating inputFieldsEntityNameOverride from ${this.inputFieldsEntityNameOverride} to ${entityName}`
      )
      this.inputFieldsEntityNameOverride = entityName
    },
    clearOverrideEntityNameForInputFields(): void {
      // ⚠ DOCUMENTED DRIFT, PRESERVED: this clears to `null` while the field's
      // initial default in entityDialogDefaults is `undefined`. So the clear
      // chain does NOT restore the initial state for this one field, and a
      // blanket `$reset()` would behave differently. Pinned by a test on
      // entityDialogState; reconcile deliberately or not at all.
      this.setOverrideEntityNameForInputFields(null)
    },

    setInputFieldsOverride(inputFields: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating inputFieldsOverride from ${this.inputFieldsOverride} to`,
        inputFields
      )
      this.inputFieldsOverride = inputFields
    },
    clearInputFieldsOverride(): void {
      this.setInputFieldsOverride(undefined)
    },

    setEntityIdOverride(id: TEntityId | null | undefined): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating entityIdOverride from ${this.entityIdOverride} to`,
        id
      )
      this.entityIdOverride = id
    },
    clearEntityIdOverride(): void {
      this.setEntityIdOverride(undefined)
    },

    setEntityData(entityData: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating entityData from ${this.entityData} to`,
        entityData
      )
      this.entityData = entityData
    },
    clearEntityData(): void {
      this.setEntityData(undefined)
    },

    setEntityParentObjectForCreate(entityParentObjectForCreate: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating entityParentObjectForCreate from ${this.entityParentObjectForCreate} to`,
        entityParentObjectForCreate
      )
      this.entityParentObjectForCreate = entityParentObjectForCreate
    },
    clearEntityParentObjectForCreate(): void {
      this.setEntityParentObjectForCreate(undefined)
    },

    setAllowAddAnother(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating allowAddAnother from ${this.allowAddAnother} to ${stateToSet}`
      )
      this.allowAddAnother = stateToSet
    },
    clearAllowAddAnother(): void {
      this.setAllowAddAnother(false)
    },

    setStartInViewMode(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating startInViewMode from ${this.startInViewMode} to ${stateToSet}`
      )
      this.startInViewMode = stateToSet
    },
    clearStartInViewMode(): void {
      this.setStartInViewMode(false)
    },

    setDisableEditButton(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating disableEditButton from ${this.disableEditButton} to ${stateToSet}`
      )
      this.disableEditButton = stateToSet
    },
    clearDisableEditButton(): void {
      this.setDisableEditButton(false)
    },

    setSuppressFocusOnFirstField(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating suppressFocusOnFirstField from ${this.suppressFocusOnFirstField} to ${stateToSet}`
      )
      this.suppressFocusOnFirstField = stateToSet
    },
    clearSuppressFocusOnFirstField(): void {
      this.setSuppressFocusOnFirstField(false)
    },

    setDoSubdueMessage(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating doSubdueMessage from ${this.doSubdueMessage} to ${stateToSet}`
      )
      this.doSubdueMessage = stateToSet
    },
    clearDoSubdueMessage(): void {
      this.setDoSubdueMessage(false)
    },

    setDoAlignMessageWithTitle(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating doAlignMessageWithTitle from ${this.doAlignMessageWithTitle} to ${stateToSet}`
      )
      this.doAlignMessageWithTitle = stateToSet
    },
    clearDoAlignMessageWithTitle(): void {
      this.setDoAlignMessageWithTitle(false)
    },

    /**
     * NOT `$reset()`. This is the ENTITY-level clear: the subentity-only fields
     * are cleared by clearSubentityDialog instead, and the two dialogs are meant
     * to reset independently. A blanket reset would collapse them — and would
     * also restore `inputFieldsEntityNameOverride` to `undefined` rather than
     * the `null` this chain sets. Both hazards are documented on
     * entityDialogDefaults; this is the code they warn about.
     */
    async clearEntityDialog(): Promise<void> {
      // eslint-disable-next-line no-console
      console.debug('-'.repeat(100))
      // eslint-disable-next-line no-console
      console.debug('store: running clearEntityDialog...')
      this.setShowEntityDialog(false)
      this.clearEntityName()
      this.clearOverrideEntityNameForInputFields()
      this.clearInputFieldsOverride()
      this.clearEntityIdOverride()
      this.clearEntityData()
      this.clearEntityParentObjectForCreate()
      this.clearEntityNameTitlePrefixOverride()
      this.clearEntityNameTitleOverride()
      this.clearSubtitle()
      this.clearMessage()
      this.clearPreRequestProcessor()
      this.clearAfterRequestCallback()
      this.clearSubentityAfterRequestCallbacks() // Don't remember if these need to be both here and in clearSubentityDialog methods
      this.clearSubentityParentObjectForCreate() // Ditto.
      this.clearStoreEditedSpecsDataAsJsonInProperty()
      this.clearAllowAddAnother()
      this.clearStartInViewMode()
      this.clearDisableEditButton()
      this.clearSuppressFocusOnFirstField()
      this.clearDoSubdueMessage()
      this.clearDoAlignMessageWithTitle()
      this.clearDesiredAction()
      await this.clearCommon()
      // eslint-disable-next-line no-console
      console.debug('-'.repeat(100))
    },

    async clearCommon(): Promise<void> {
      this.clearDeleteButtonCaptionsVerbOverride()
      this.clearSaveButtonCaptionVerbOverride()
    },

    setShowSubentityDialog(stateToSet: boolean): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating showSubentityDialog from ${this.showSubentityDialog} to ${stateToSet}`
      )
      this.showSubentityDialog = stateToSet
    },

    setSubentityName(entityName: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating subentityName from '${this.subentityName}' to '${entityName}'`
      )
      this.subentityName = entityName
    },
    clearSubentityName(): void {
      this.setSubentityName(null)
    },

    setSubentityNameTitleOverride(override: string | null): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating subentityNameTitleOverride from ${this.subentityNameTitleOverride} to ${override}`
      )
      this.subentityNameTitleOverride = override
    },
    clearSubentityNameTitleOverride(): void {
      this.setSubentityNameTitleOverride(null)
    },

    setSubentityInputFieldsEntityNameOverride(
      override: string | null | undefined
    ): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating subentityInputFieldsEntityNameOverride from ${this.subentityInputFieldsEntityNameOverride} to ${override}`
      )
      this.subentityInputFieldsEntityNameOverride = override
    },
    clearSubentityInputFieldsEntityNameOverride(): void {
      this.setSubentityInputFieldsEntityNameOverride(null)
    },

    // No clear counterpart, as in the Vuex module.
    setSubentityReturnValue(value: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating subentityReturnValue from '${this.subentityReturnValue}' to`,
        value
      )
      this.subentityReturnValue = value
    },

    setSubentityAfterRequestProcessor(fn: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        'store: mutating subentityAfterRequestProcessor from',
        this.subentityAfterRequestProcessor,
        'to',
        fn
      )
      this.subentityAfterRequestProcessor = fn
    },
    clearSubentityAfterRequestProcessor(): void {
      this.setSubentityAfterRequestProcessor(undefined)
    },

    setSubentityAfterRequestCallbacks(callbacksArray: any): void {
      // eslint-disable-next-line no-console
      console.debug(
        'store: mutating subentityAfterRequestCallbacks from',
        this.subentityAfterRequestCallbacks,
        'to',
        callbacksArray
      )
      this.subentityAfterRequestCallbacks = callbacksArray
    },
    clearSubentityAfterRequestCallbacks(): void {
      this.setSubentityAfterRequestCallbacks(undefined)
    },

    setSubentityParentObjectForCreate(
      subentityParentObjectForCreate: any
    ): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating subentityParentObjectForCreate from '${this.subentityParentObjectForCreate}' to`,
        subentityParentObjectForCreate
      )
      this.subentityParentObjectForCreate = subentityParentObjectForCreate
    },
    clearSubentityParentObjectForCreate(): void {
      this.setSubentityParentObjectForCreate(undefined)
    },

    setValuesForSubentityFieldsWhoseValuesToSetFromEntityFields(
      values: any
    ): void {
      // eslint-disable-next-line no-console
      console.debug(
        `store: mutating valuesForSubentityFieldsWhoseValuesToSetFromEntityFields from '${this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields}' to`,
        values
      )
      this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields = values
    },
    clearValuesForSubentityFieldsWhoseValuesToSetFromEntityFields(): void {
      this.setValuesForSubentityFieldsWhoseValuesToSetFromEntityFields(null)
    },

    /** The SUBENTITY-level clear. See clearEntityDialog on why neither is $reset(). */
    async clearSubentityDialog(): Promise<void> {
      // eslint-disable-next-line no-console
      console.debug('-'.repeat(100))
      // eslint-disable-next-line no-console
      console.debug('store: running clearSubentityDialog...')
      this.setShowSubentityDialog(false)
      this.clearSubentityName()
      this.clearSubentityNameTitleOverride()
      this.clearSubentityInputFieldsEntityNameOverride()
      this.clearSubentityAfterRequestProcessor()
      this.clearSubentityParentObjectForCreate()
      this.clearValuesForSubentityFieldsWhoseValuesToSetFromEntityFields()
      await this.clearCommon()
      // eslint-disable-next-line no-console
      console.debug('-'.repeat(100))
    },

    async clearEntityAndSubentityDialogs(): Promise<void> {
      await this.clearEntityDialog()
      await this.clearSubentityDialog() // There is some duplication here. Both run clearCommon.
    },
  },
})
